// =============================================================================
// useAuth Hook
// =============================================================================

import { useState, useCallback, useEffect } from 'react';
import { useLogCollectorClient, setStoredToken } from './useLogCollectorClient.js';
import { useSessionExpiredState } from './context.js';
import { ApiClientError } from '../lib/api.js';

// =============================================================================
// Constants
// =============================================================================

const STORAGE_KEY = 'log-collector-token';

// =============================================================================
// Types
// =============================================================================

export interface UseAuthOptions {
  /** Base URL override */
  baseUrl?: string;
  /** Whether to persist token in localStorage (default: true) */
  persist?: boolean;
  /** Callback when authentication state changes */
  onAuthChange?: (isAuthenticated: boolean) => void;
}

export interface UseAuthResult {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether a login/logout operation is in progress */
  isLoading: boolean;
  /** Error from the last operation */
  error: string | null;
  /** Login with password */
  login: (password: string) => Promise<boolean>;
  /** Logout and clear session */
  logout: () => Promise<void>;
  /** Clear error state */
  clearError: () => void;
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook for managing authentication state.
 *
 * @example
 * ```tsx
 * function LoginForm() {
 *   const { isAuthenticated, isLoading, error, login, logout } = useAuth();
 *
 *   if (isAuthenticated) {
 *     return <button onClick={logout}>Logout</button>;
 *   }
 *
 *   const handleSubmit = async (password: string) => {
 *     const success = await login(password);
 *     if (success) {
 *       // Navigate to dashboard
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={(e) => { e.preventDefault(); handleSubmit(password); }}>
 *       {error && <div className="error">{error}</div>}
 *       <input type="password" disabled={isLoading} />
 *       <button type="submit" disabled={isLoading}>Login</button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useAuth(options: UseAuthOptions = {}): UseAuthResult {
  const { baseUrl, persist = true, onAuthChange } = options;
  const { client } = useLogCollectorClient({ baseUrl });

  // Get session expired state from context (returns false if not in provider)
  const { isSessionExpired, resetSessionExpired } = useSessionExpiredState();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    // Check if we have a stored token
    if (typeof window === 'undefined') return false;
    try {
      return localStorage.getItem(STORAGE_KEY) !== null;
    } catch {
      return false;
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If session was expired by 401, immediately update auth state
  useEffect(() => {
    if (isSessionExpired && isAuthenticated) {
      setIsAuthenticated(false);
    }
  }, [isSessionExpired, isAuthenticated]);

  // Sync authentication state with client
  useEffect(() => {
    const hasToken = client.getSessionToken() !== null;
    if (hasToken !== isAuthenticated && !isSessionExpired) {
      setIsAuthenticated(hasToken);
    }
  }, [client, isAuthenticated, isSessionExpired]);

  // Notify on auth state changes
  useEffect(() => {
    onAuthChange?.(isAuthenticated);
  }, [isAuthenticated, onAuthChange]);

  /**
   * Login with password.
   */
  const login = useCallback(
    async (password: string): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await client.login(password);

        if (response.success && response.token) {
          if (persist) {
            setStoredToken(response.token);
          }
          // Reset session expired state before setting authenticated
          resetSessionExpired();
          setIsAuthenticated(true);
          return true;
        }

        setError(response.error ?? 'Login failed');
        return false;
      } catch (err) {
        if (err instanceof ApiClientError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unexpected error occurred');
        }
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [client, persist, resetSessionExpired]
  );

  /**
   * Logout and clear session.
   */
  const logout = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await client.logout();
    } catch {
      // Ignore logout errors
    } finally {
      if (persist) {
        setStoredToken(null);
      }
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  }, [client, persist]);

  /**
   * Clear error state.
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  return {
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    clearError,
  };
}
